package rendering

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import domain.COLORS
import kotlin.math.exp
import kotlin.math.ln
import kotlin.random.Random

// ── Particle model ────────────────────────────────────────────────────────────

/**
 * A single debris particle spawned when a line is cleared.
 *
 * @param x         Current X position in board-pixel space.
 * @param y         Current Y position in board-pixel space.
 * @param vx        Horizontal velocity (pixels per ms).
 * @param vy        Vertical velocity (pixels per ms).
 * @param size      Particle size in pixels.
 * @param color     Particle colour (derived from the cleared cell's piece type).
 * @param life      Remaining lifetime in ms (0 = dead).
 * @param maxLife   Initial lifetime used to compute alpha fade.
 */
data class Particle(
    var x: Float,
    var y: Float,
    var vx: Float,
    var vy: Float,
    val size: Float,
    val color: Color,
    var life: Float,
    val maxLife: Float,
)

// ── DebrisState ───────────────────────────────────────────────────────────────

/**
 * Manages the mutable list of debris [Particle]s produced by line clears.
 *
 * Usage:
 * ```kotlin
 * val debris = remember { DebrisState() }
 *
 * // On line clear event:
 * debris.triggerLineClearDebris(clearedRows = event.linesCleared, getCell = { r, c -> board[r, c] })
 *
 * // Each frame inside a Canvas:
 * debris.update(deltaMs)
 * drawDebris(debris)
 * ```
 */
class DebrisState {

    private val _particles = mutableListOf<Particle>()

    /** True when there are live particles to draw. */
    val hasParticles: Boolean get() = _particles.isNotEmpty()

    // ── Physics update ────────────────────────────────────────────────────────

    /**
     * Advances all particles by [deltaMs] milliseconds and removes dead ones.
     *
     * Physics model:
     * - Constant downward gravity (0.0008 px/ms²)
     * - Horizontal drag (0.98 per ms, applied as exponential decay)
     * - Life decremented by [deltaMs]; particle removed when life ≤ 0
     */
    fun update(deltaMs: Float) {
        val gravity = 0.0008f * deltaMs
        val drag = exp(ln(0.98) * (deltaMs / 16f)).toFloat() // normalised to ~60 fps

        val iter = _particles.iterator()
        while (iter.hasNext()) {
            val p = iter.next()
            p.vy += gravity
            p.vx *= drag
            p.x += p.vx * deltaMs
            p.y += p.vy * deltaMs
            p.life -= deltaMs
            if (p.life <= 0f) iter.remove()
        }
    }

    // ── Spawn ─────────────────────────────────────────────────────────────────

    /**
     * Spawns debris particles for each cell in [clearedRows].
     *
     * @param clearedRows  Set of board row indices that were cleared.
     * @param getCell      Returns the piece-type ID at (row, col) **before** the
     *                     clear animation removes them from the board.
     * @param blockSize    Board cell size in pixels (default [BLOCK_SIZE]).
     * @param bufferSize   Board buffer rows (default [BOARD_BUFFER]).
     */
    fun triggerLineClearDebris(
        clearedRows: Collection<Int>,
        getCell: (row: Int, col: Int) -> Int,
        blockSize: Float = BLOCK_SIZE,
        bufferSize: Int = BOARD_BUFFER,
    ) {
        val rng = Random.Default
        for (row in clearedRows) {
            for (col in 0 until BOARD_COLS) {
                val cellId = getCell(row, col)
                val baseColor = (COLORS[cellId] ?: 0x888888).toColor()

                // Spawn 2–4 particles per cell
                val count = rng.nextInt(2, 5)
                repeat(count) {
                    val cx = (col + 0.5f) * blockSize
                    val cy = (row - bufferSize + 0.5f) * blockSize

                    val angle = rng.nextFloat() * 2f * PI
                    val speed = rng.nextFloat() * 0.25f + 0.05f // px/ms

                    _particles += Particle(
                        x = cx,
                        y = cy,
                        vx = kotlin.math.cos(angle.toDouble()).toFloat() * speed,
                        vy = kotlin.math.sin(angle.toDouble()).toFloat() * speed - 0.1f,
                        size = rng.nextFloat() * (blockSize * 0.35f) + (blockSize * 0.1f),
                        color = baseColor,
                        life = rng.nextFloat() * 400f + 300f, // 300–700 ms
                        maxLife = 700f,
                    )
                }
            }
        }
    }

    /** Removes all particles immediately. */
    fun clear() = _particles.clear()

    // ── Internal helpers ──────────────────────────────────────────────────────

    internal fun particles(): List<Particle> = _particles
}

// ── DrawScope extension ───────────────────────────────────────────────────────

/**
 * Batch-draws all live particles in [state] using [DrawScope.drawRect].
 * Alpha is linearly faded based on remaining life.
 */
fun DrawScope.drawDebris(state: DebrisState) {
    for (p in state.particles()) {
        val alpha = (p.life / p.maxLife).coerceIn(0f, 1f)
        drawRect(
            color = p.color.copy(alpha = alpha),
            topLeft = Offset(p.x - p.size / 2f, p.y - p.size / 2f),
            size = Size(p.size, p.size),
        )
    }
}

// ── Kotlin stdlib shims ───────────────────────────────────────────────────────

private const val PI = kotlin.math.PI.toFloat()

private fun Int.toColor(alpha: Float = 1f): Color {
    val r = ((this shr 16) and 0xFF) / 255f
    val g = ((this shr 8) and 0xFF) / 255f
    val b = (this and 0xFF) / 255f
    return Color(r, g, b, alpha)
}
